import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Col, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { toast } from "react-toastify";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import FixedSave from "../../../../components/Actions/FixedSave";
import CustomSelect from "../../../../components/Forms/CustomSelect";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { get, post } from "../../../../utils/helpers/api";
import { useSettings } from "../../../../context/settings";
import { calculateSalary } from "../../utils/payroll";
import { DOCUMENT_TYPES } from "../../utils/documents";
import DocumentSheet from "./Print/DocumentSheet";

const toDateString = (value) => (value ? format(new Date(value), 'yyyy-MM-dd') : null);

/** Payload fields that hold a date and therefore need serialising on save. */
const DATE_FIELDS = ['end_date', 'departure_date', 'return_date', 'effective_date', 'from_date', 'to_date'];

function CreateDocumentPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();

  const [letterhead, setLetterhead] = useState({});
  const [employee, setEmployee] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [documentData, setDocumentData] = useState({
    employee_id: null,
    type: null,
    issue_date: new Date(),
    issue_place: '',
    signatory_name: '',
    signatory_title: 'Le Directeur Général',
    notes: '',
  });
  const [payload, setPayload] = useState({});

  useEffect(() => {
    const loadLetterhead = async () => {
      const data = await get('/hr/documents/create');
      if (data?.success) {
        setLetterhead(data.letterhead || {});
        setDocumentData(current => ({
          ...current,
          issue_place: current.issue_place || data.default_issue_place || '',
        }));
      }
    };
    loadLetterhead();
  }, []);

  const selectedType = DOCUMENT_TYPES.find(type => type.value === documentData.type);
  const hasField = (field) => selectedType?.fields.includes(field);

  const updateDocument = (field, value) => setDocumentData({ ...documentData, [field]: value });
  const updatePayload = (field, value) => setPayload({ ...payload, [field]: value });

  /** Same shape the API stores, so the preview and the printed page agree. */
  const previewDocument = useMemo(() => {
    if (!documentData.type || !employee) return null;

    const snapshot = {
      company: letterhead,
      employee: {
        firstname: employee.firstname,
        lastname: employee.lastname,
        full_name: `${employee.firstname} ${employee.lastname ?? ''}`.trim(),
        gender: employee.gender,
        birthdate: employee.birthdate,
        birthplace: payload.birthplace,
        address: employee.address,
        city: employee.city,
        job_title: employee.job_title,
        department: employee.department,
        contract_start_date: employee.contract_start_date,
        contract_end_date: employee.contract_end_date,
        social_security_number: employee.social_security_number,
      },
    };

    if (documentData.type === 'attestation_salaire') {
      const breakdown = calculateSalary(employee.salary_amount, employee.benefits || []);
      snapshot.salary = {
        basic_salary: breakdown.basicSalary,
        allowances: breakdown.allowances,
        deductions: breakdown.deductions,
        gross_salary: breakdown.grossSalary,
        net_salary: breakdown.netSalary,
      };
    }

    return {
      reference: `___/DRH/${new Date(documentData.issue_date || Date.now()).getFullYear()}`,
      type: documentData.type,
      issue_date: documentData.issue_date,
      issue_place: documentData.issue_place,
      signatory_name: documentData.signatory_name,
      signatory_title: documentData.signatory_title,
      status: 'issued',
      payload,
      snapshot,
    };
  }, [documentData, employee, payload, letterhead]);

  const saveDocument = async () => {
    setSaveLoading(true);

    const serialisedPayload = { ...payload };
    DATE_FIELDS.forEach(field => {
      if (serialisedPayload[field]) {
        serialisedPayload[field] = toDateString(serialisedPayload[field]);
      }
    });

    const data = await post('/hr/documents/store', {
      employee_id: documentData.employee_id,
      type: documentData.type,
      issue_date: toDateString(documentData.issue_date),
      issue_place: documentData.issue_place || null,
      signatory_name: documentData.signatory_name || null,
      signatory_title: documentData.signatory_title || null,
      notes: documentData.notes || null,
      payload: serialisedPayload,
    });

    if (data?.success) {
      toast.success(t('hr.documents.issued'));
      navigate(`/hr/documents/view/${data.document.id}`);
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  const dateField = (field, label) => (
    <Col md="4" className="mb-3" key={field}>
      <Label>{label}</Label>
      <DatePicker
        selected={payload[field] || null}
        onChange={(date) => updatePayload(field, date)}
        className="form-control"
        dateFormat={datePickerFormat}
        placeholderText={datePickerFormat.toLowerCase()}
      />
    </Col>
  );

  const textField = (field, label, placeholder) => (
    <Col md="4" className="mb-3" key={field}>
      <Label>{label}</Label>
      <Input
        value={payload[field] || ''}
        onChange={(e) => updatePayload(field, e.target.value)}
        placeholder={placeholder}
      />
    </Col>
  );

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('sidebar.documents'), t('hr.documents.newDocument')]}
          onBack={() => navigate('/hr/documents')}
        />

        <div className="subheader-page-content scrollable">
          <Row>
            {/* ── Form ── */}
            <Col lg="5">
              <Card className="mb-3">
                <CardBody>
                  <Row>
                    <Col md="12" className="mb-3">
                      <Label>{t('hr.employeeName')}</Label>
                      <CustomSelect
                        value={documentData.employee_id ? { value: documentData.employee_id, label: `${employee?.firstname ?? ''} ${employee?.lastname ?? ''}`.trim() } : null}
                        onChange={(selected, objectData) => {
                          updateDocument('employee_id', selected?.value ?? null);
                          setEmployee(objectData ?? null);
                        }}
                        module="hr/employees"
                        responseKey="employees"
                        placeholder={t('hr.documents.searchEmployee')}
                      />
                    </Col>

                    <Col md="12" className="mb-3">
                      <Label>{t('hr.documentType')}</Label>
                      <Select
                        options={DOCUMENT_TYPES}
                        styles={SelectCustomStyles}
                        placeholder={t('common.select')}
                        onChange={(option) => {
                          updateDocument('type', option?.value ?? null);
                          setPayload({});
                        }}
                      />
                      {selectedType && (
                        <small className="text-muted d-block mt-1">{selectedType.hint}</small>
                      )}
                    </Col>
                  </Row>

                  {selectedType && (
                    <Row>
                      {hasField('birthplace') && textField('birthplace', 'Lieu de naissance', 'Alger')}
                      {hasField('end_date') && dateField('end_date', 'Date de fin de contrat')}
                      {hasField('positions_held') && textField('positions_held', 'Poste(s) occupé(s)', 'Ingénieur, puis Chef de projet')}
                      {hasField('purpose') && textField('purpose', documentData.type === 'ordre_mission' ? 'Objet de la mission' : 'Objet de la demande', documentData.type === 'ordre_mission' ? 'Réunion client' : 'demande de visa')}
                      {hasField('destination') && textField('destination', 'Destination', 'Oran')}
                      {hasField('transport') && textField('transport', 'Moyen de transport', 'Véhicule de service')}
                      {hasField('departure_date') && dateField('departure_date', 'Date de départ')}
                      {hasField('return_date') && dateField('return_date', 'Date de retour')}
                      {hasField('new_position') && textField('new_position', 'Nouveau poste', 'Chef de département')}
                      {hasField('new_department') && textField('new_department', 'Nouveau département', 'Finance')}
                      {hasField('effective_date') && dateField('effective_date', "Date d'effet")}
                      {hasField('hours') && (
                        <Col md="4" className="mb-3">
                          <Label>Nombre d'heures</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.5"
                            value={payload.hours || ''}
                            onChange={(e) => updatePayload('hours', e.target.value)}
                          />
                        </Col>
                      )}
                      {hasField('from_date') && dateField('from_date', 'Du')}
                      {hasField('to_date') && dateField('to_date', 'Au')}
                      {hasField('reason') && textField('reason', 'Motif', 'Surcroît d\'activité')}
                      {hasField('expenses_covered') && (
                        <Col md="12" className="mb-3 d-flex align-items-center">
                          <Input
                            type="checkbox"
                            className="mt-0"
                            checked={!!payload.expenses_covered}
                            onChange={(e) => updatePayload('expenses_covered', e.target.checked)}
                          />
                          <Label className="ms-2 mb-0">Frais de mission à la charge de l'entreprise</Label>
                        </Col>
                      )}
                    </Row>
                  )}

                  <hr />

                  <Row>
                    <Col md="6" className="mb-3">
                      <Label>{t('hr.documents.issueDate')}</Label>
                      <DatePicker
                        selected={documentData.issue_date}
                        onChange={(date) => updateDocument('issue_date', date)}
                        className="form-control"
                        dateFormat={datePickerFormat}
                      />
                    </Col>
                    <Col md="6" className="mb-3">
                      <Label>{t('hr.documents.issuePlace')}</Label>
                      <Input
                        value={documentData.issue_place}
                        onChange={(e) => updateDocument('issue_place', e.target.value)}
                        placeholder="Alger"
                      />
                    </Col>
                    <Col md="6" className="mb-3">
                      <Label>{t('hr.documents.signatoryTitle')}</Label>
                      <Input
                        value={documentData.signatory_title}
                        onChange={(e) => updateDocument('signatory_title', e.target.value)}
                      />
                    </Col>
                    <Col md="6" className="mb-3">
                      <Label>{t('hr.documents.signatoryName')}</Label>
                      <Input
                        value={documentData.signatory_name}
                        onChange={(e) => updateDocument('signatory_name', e.target.value)}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* ── Live preview ── */}
            <Col lg="7">
              <h6 className="fw-bold mb-2">{t('hr.documents.preview')}</h6>
              {previewDocument ? (
                <div style={{ transform: 'scale(0.72)', transformOrigin: 'top left', width: '139%' }}>
                  <DocumentSheet document={previewDocument} />
                </div>
              ) : (
                <Card>
                  <CardBody className="text-center text-muted py-5">
                    {t('hr.documents.previewHint')}
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        </div>
      </div>

      <FixedSave
        onSave={saveDocument}
        loading={saveLoading}
        disabled={saveLoading || !documentData.employee_id || !documentData.type}
      />
    </SidebarLayout>
  );
}

export default CreateDocumentPage;
